import { volumeApiFetch } from "@/lib/volume-api";
import { z } from "zod/v4";

export const OptimusBeneficiariesResponseSchema = z.array(
  z.object({
    applicationId: z.string().nullable(),
    optimusBeneficiaryId: z.string(),
    optimusExternalCustomerId: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
    updatedAt: z.string(),
    updatedBy: z.string(),
    firstNames: z.string(),
    lastNames: z.string(),
    businessName: z.string().nullable(),
    contactDetails: z.object({
      email: z.string(),
      mobilePhone: z
        .object({
          countryCode: z.string(),
          localNumber: z.string(),
        })
        .nullable(),
      homePhone: z
        .object({
          countryCode: z.string(),
          localNumber: z.string(),
        })
        .nullable(),
      workPhone: z
        .object({
          countryCode: z.string(),
          localNumber: z.string(),
        })
        .nullable(),
    }),
    currency: z.string(),
    countryCode: z.string(),
    accountType: z.string(),
    accountNumber: z.string().nullable(),
    sortCode: z.string().nullable(),
    iban: z.string().nullable(),
    status: z.string().nullable(),
    lifecycleStatus: z.string().nullable().optional(),
    address: z
      .object({
        addressLine1: z.string().nullable(),
        country: z.string().nullable(),
        postCode: z.string().nullable(),
      })
      .nullable(),
    bic: z.string().nullable(),
    lei: z.string().nullable(),
    otherIdentifier: z.string().nullable(),
    otherIdentifierType: z.string().nullable(),
    isSwift: z.boolean(),
    purposeCode: z.string().nullable(),
    purposeCategoryCode: z.string().nullable(),
    beneficiaryType: z.enum(["SWIFT", "FASTER_PAYMENTS"]),
  }),
);
export type FetchBeneficiariesResponse = z.infer<
  typeof OptimusBeneficiariesResponseSchema
>;

export function isUsableOptimusBeneficiary(
  beneficiary: FetchBeneficiariesResponse[number],
) {
  return beneficiary.lifecycleStatus === "ACTIVE";
}

export async function fetchOptimusBeneficiariesForCustomer({
  customerId,
  accessToken,
}: {
  customerId: string;
  accessToken: string;
}) {
  const response = await volumeApiFetch({
    accessToken,
    endpoint: `providers/optimus/customers/${customerId}/beneficiaries`,
    method: "GET",
  });

  if (response.status === 401 || response.status === 403) {
    throw new Error("Unauthorized");
  }
  if (!response.ok) {
    throw new Error("Failed to fetch optimus beneficiaries");
  }
  const json = await response.json();
  return OptimusBeneficiariesResponseSchema.parse(json);
}
